
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useNavigate } from 'react-router-dom';

interface KpiCardProps {
  title: string;
  value: string | number;
  trend?: number;
  color?: string;
  progressValue?: number;
  filterLink?: string; // URL to navigate to
  tooltipText?: string; // Tooltip text for the card
}

const KpiCard: React.FC<KpiCardProps> = ({ 
  title, 
  value, 
  trend = 0,
  color = 'bg-accent/20',
  progressValue = 75,
  filterLink,
  tooltipText = "Click to view details"
}) => {
  const isPositive = trend >= 0;
  const navigate = useNavigate();
  
  const handleClick = () => {
    if (filterLink) {
      navigate(filterLink);
    }
  };
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Card 
            className={cn(
              "overflow-hidden transition-all", 
              filterLink && "cursor-pointer hover:shadow-md hover:border-primary/20"
            )}
            onClick={handleClick}
          >
            <CardContent className="p-6">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">{title}</p>
                  <p className="text-2xl font-bold">{value}</p>
                </div>
                <div className={cn(
                  "h-12 w-12 rounded-full flex items-center justify-center",
                  color
                )}>
                  {isPositive ? (
                    <TrendingUp className="h-6 w-6 text-accent" />
                  ) : (
                    <TrendingDown className="h-6 w-6 text-status-red" />
                  )}
                </div>
              </div>
              
              <div className="mt-4">
                <div className="flex justify-between items-center mb-1">
                  <div className={cn(
                    "text-xs font-medium flex items-center gap-1",
                    isPositive ? "text-accent" : "text-status-red"
                  )}>
                    {isPositive ? "+" : ""}{trend}% vs last week
                  </div>
                </div>
                <Progress value={progressValue} className="h-1.5" />
              </div>
            </CardContent>
          </Card>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          <p>{tooltipText}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default KpiCard;
